import { Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/authStore";
import { useUserApplications } from "@/hooks/useUserApplications";

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, permissions } = useAuthStore();
  const location = useLocation();
  const { applications, loading: appsLoading } = useUserApplications();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-4 text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth/login" replace />;
  }

  if (appsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="mt-4 text-sm text-muted-foreground">Loading applications...</p>
        </div>
      </div>
    );
  }

  // Core shell route checks.
  const isDashboardRoute =
    location.pathname === "/dashboard" || location.pathname.startsWith("/dashboard/");
  const isSettingsRoute =
    location.pathname === "/settings" || location.pathname.startsWith("/settings/");

  const normalizedPermissions = new Set(
    (permissions || []).map((permission) => permission.toLowerCase().replace(/[\s_-]/g, ""))
  );
  const canAccessSettings =
    normalizedPermissions.has("admin") || normalizedPermissions.has("superadmin");

  if (isSettingsRoute && !canAccessSettings) {
    return <Navigate to="/dashboard" replace />;
  }

  const isShellRoute = isDashboardRoute || isSettingsRoute;
  if (isShellRoute) {
    return <>{children}</>;
  }

  // API-driven app access check.
  const canAccessRoute = applications.some((app) =>
    location.pathname === app.route || location.pathname.startsWith(`${app.route}/`)
  );
  if (!canAccessRoute) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
